import React, { useState, useEffect, useRef } from 'react';
import mammoth from 'mammoth';
import { Nav, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './UpdatedInstructionsPage.css';

const InstructionsFromDocx = () => {
  const [toc, setToc] = useState([]);
  const [htmlContent, setHtmlContent] = useState('');
  const [expandedSection, setExpandedSection] = useState(null); 
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const [parsedDocument, setParsedDocument] = useState(null); // Store parsed document for full-text search

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchInputRef.current && !searchInputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Add a temporary highlight to show where we scrolled to
      el.style.backgroundColor = '#cbf2d3';
      setTimeout(() => {
        el.style.backgroundColor = '';
      }, 2000);
    }
  };

  const flattenToc = (toc) => {
    const results = [];
    toc.forEach(section => {
      results.push({ id: section.id, title: section.title, type: 'section' });

      section.subsections?.forEach(sub => {
        results.push({ id: sub.id, title: sub.title, type: 'subsection', parentId: section.id });

        sub.subsubsections?.forEach(subsub => {
          results.push({ id: subsub.id, title: subsub.title, type: 'subsubsection', parentId: sub.id });
        });
      });
    });
    return results;
  };

  const generateId = (text) => text.toLowerCase().replace(/[^ -\w]+/g, '-');

  useEffect(() => {
    loadFileFromPublic();
  }, []);

  const generateUniqueIds = (headings) => {
    const idCounts = {};
    return headings.map(h => {
      const tag = h.tagName.toLowerCase();
      const title = h.textContent.trim();
      let baseId = title.toLowerCase().replace(/[^ -\w]+/g, '-');

      // Ensure unique ID
      if (idCounts[baseId]) {
        idCounts[baseId]++;
        baseId = `${baseId}-${idCounts[baseId]}`;
      } else {
        idCounts[baseId] = 1;
      }

      h.id = baseId;
      return { element: h, tag, title, id: baseId };
    });
  };

  const loadFileFromPublic = async (e) => {
    const response = await fetch('/Provider%20Portal%20-%20IFU%20-%20Rev%204%20with%20Headers.docx');
    const arrayBuffer = await response.arrayBuffer();
    const { value: htmlString } = await mammoth.convertToHtml({ arrayBuffer });

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const rawHeadings = [...doc.querySelectorAll('h1,h2,h3')];
    const parsedHeadings = generateUniqueIds(rawHeadings);

    // Build hierarchical TOC: h1 with h2 children
    const tocItems = [];
    let currentH1 = null;
    let currentH2 = null;
    parsedHeadings.forEach(({ tag, title, id, element }) => {
      if (tag === 'h1') {
        currentH1 = { title, id, subsections: [] };
        tocItems.push(currentH1);
        currentH2 = null;
      } else if (tag === 'h2' && currentH1) {
        currentH2 = { title, id, subsubsections: [] };
        currentH1.subsections.push(currentH2);
      } else if (tag === 'h3' && currentH2) {
        currentH2.subsubsections.push({ title, id });
      }
    });

    // Find the disclaimer paragraph and set content from there
    const disclaimerText =
      'All patient names, photographs, medical conditions, healthcare provider information, facility names, and other data shown in this document are entirely fictional and used for demonstration purposes only. Any resemblance to real persons, medical conditions, or healthcare facilities is purely coincidental.';
    const paragraphs = Array.from(doc.body.querySelectorAll('p'));
    let startIndex = paragraphs.findIndex(p => p.textContent.trim().startsWith(disclaimerText));
    let contentHtml = '';
    if (startIndex !== -1) {
      // Include the disclaimer and everything after
      const nodes = [];
      for (let i = startIndex + 1; i < doc.body.children.length; i++) {
        nodes.push(doc.body.children[i].outerHTML);
      }
      contentHtml = nodes.join('');
    } else {
      // Fallback: show all content
      contentHtml = doc.body.innerHTML;
    }

    setToc(tocItems);
    setHtmlContent(contentHtml);
    setParsedDocument(doc); // Store the parsed document for full-text search
    setExpandedSection(tocItems.length > 0 ? tocItems[0].id : null); // Auto-expand first section
  };

  const findNearestHeading = (element) => {
    // Walk up the DOM tree to find the nearest heading
    let current = element;
    while (current && current !== document.body) {
      const tagName = current.tagName?.toLowerCase();
      if (tagName && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName) && current.id) {
        return current.id;
      }
      current = current.parentElement;
    }
    return null;
  };

  const findNearestHeadingId = (element) => {
    // Walk up the DOM tree to find the nearest heading
    let current = element;
    while (current && current !== document.body) {
      const tagName = current.tagName?.toLowerCase();
      if (tagName && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
        // If heading has an ID, use it
        if (current.id) {
          return current.id;
        }
        // Otherwise, try to match by text content with TOC
        const headingText = current.textContent?.trim();
        if (headingText) {
          // Find matching heading in TOC
          for (const section of toc) {
            if (section.title === headingText && section.id) {
              return section.id;
            }
            for (const sub of section.subsections || []) {
              if (sub.title === headingText && sub.id) {
                return sub.id;
              }
              for (const subsub of sub.subsubsections || []) {
                if (subsub.title === headingText && subsub.id) {
                  return subsub.id;
                }
              }
            }
          }
        }
      }
      current = current.parentElement;
    }
    return null;
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const lower = query.toLowerCase().trim();
    const results = [];
    const seenIds = new Set(); // Track seen heading IDs to avoid duplicates

    // Search through TOC headings first
    toc.forEach((section) => {
      if (section.title.toLowerCase().includes(lower)) {
        results.push({ type: 'section', title: section.title, id: section.id });
        seenIds.add(section.id);
      }
      section.subsections?.forEach((sub) => {
        if (sub.title.toLowerCase().includes(lower)) {
          results.push({ type: 'subsection', title: sub.title, id: sub.id, parentId: section.id });
          seenIds.add(sub.id);
        }
        sub.subsubsections?.forEach((subsub) => {
          if (subsub.title.toLowerCase().includes(lower)) {
            results.push({ type: 'subsubsection', title: subsub.title, id: subsub.id, parentId: sub.id, grandParentId: section.id });
            seenIds.add(subsub.id);
          }
        });
      });
    });

    // Search through rendered document content
    const mainContent = document.querySelector('.main-content-instructions');
    
    // Also search through HTML string as fallback
    if (htmlContent && htmlContent.toLowerCase().includes(lower)) {
      // Parse HTML to search through it
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      
      const allElements = tempDiv.querySelectorAll('p, li, td, span, div, h1, h2, h3, h4, h5, h6, strong, em, u, b, i, label');
      
      allElements.forEach((element) => {
        const text = element.textContent || '';
        const elementText = text.trim();
        
        // Skip if empty or too short
        if (!elementText || elementText.length < 3) return;
        
        // Check if this element contains the search query
        if (elementText.toLowerCase().includes(lower)) {
          // Find the nearest heading by walking up
          let current = element;
          let nearestHeadingId = null;
          
          while (current && current !== tempDiv) {
            const tagName = current.tagName?.toLowerCase();
            if (tagName && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
              // Try to find ID or match by text
              if (current.id) {
                nearestHeadingId = current.id;
                break;
              }
              // Match by text content
              const headingText = current.textContent?.trim();
              if (headingText) {
                for (const section of toc) {
                  if (section.title === headingText && section.id) {
                    nearestHeadingId = section.id;
                    break;
                  }
                  for (const sub of section.subsections || []) {
                    if (sub.title === headingText && sub.id) {
                      nearestHeadingId = sub.id;
                      break;
                    }
                    for (const subsub of sub.subsubsections || []) {
                      if (subsub.title === headingText && subsub.id) {
                        nearestHeadingId = subsub.id;
                        break;
                      }
                    }
                    if (nearestHeadingId) break;
                  }
                  if (nearestHeadingId) break;
                }
                if (nearestHeadingId) break;
              }
            }
            current = current.parentElement;
          }
          
          if (nearestHeadingId) {
            const matchKey = nearestHeadingId + '-' + elementText.substring(0, 50);
            if (!seenIds.has(matchKey)) {
              seenIds.add(matchKey);
              
              // Find the parent section
              let parentSection = null;
              for (const section of toc) {
                if (section.id === nearestHeadingId) {
                  parentSection = section;
                  break;
                }
                for (const sub of section.subsections || []) {
                  if (sub.id === nearestHeadingId) {
                    parentSection = section;
                    break;
                  }
                  for (const subsub of sub.subsubsections || []) {
                    if (subsub.id === nearestHeadingId) {
                      parentSection = section;
                      break;
                    }
                  }
                  if (parentSection) break;
                }
                if (parentSection) break;
              }

              // Extract a snippet around the match
              const index = elementText.toLowerCase().indexOf(lower);
              const start = Math.max(0, index - 30);
              const end = Math.min(elementText.length, index + lower.length + 70);
              let snippet = elementText.substring(start, end);
              if (start > 0) snippet = '...' + snippet;
              if (end < elementText.length) snippet = snippet + '...';
              
              // Highlight the search term in the snippet
              const highlightedSnippet = snippet.replace(
                new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
                '<strong style="background-color: #fff3cd; font-weight: 600;">$1</strong>'
              );

              results.push({
                type: 'content',
                title: snippet,
                highlightedTitle: highlightedSnippet,
                id: nearestHeadingId,
                parentId: parentSection?.id,
                fullText: elementText.substring(0, 200),
                elementText: elementText
              });
            }
          }
        }
      });
    }
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    // Also search through rendered DOM if available
    if (mainContent) {
      // Use a tree walker to search through all text nodes in the rendered content
      const walker = document.createTreeWalker(
        mainContent,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: function(node) {
            // Skip text nodes inside script, style, or mark tags
            const parent = node.parentElement;
            if (parent && (parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE' || parent.tagName === 'MARK')) {
              return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
          }
        },
        false
      );

      const textMatches = [];
      let node;
      while (node = walker.nextNode()) {
        const text = node.textContent || '';
        if (text.toLowerCase().includes(lower)) {
          const parentElement = node.parentElement;
          if (parentElement) {
            textMatches.push({
              node: node,
              element: parentElement,
              text: text.trim()
            });
          }
        }
      }

      // Process matches and find their nearest headings
      textMatches.forEach(({ element, text: elementText }) => {
        if (!elementText || elementText.length < 3) return;
        
        // Find the nearest heading ID
        const nearestHeadingId = findNearestHeadingId(element);
        
        if (nearestHeadingId) {
          const matchKey = nearestHeadingId + '-' + elementText.substring(0, 50);
          if (!seenIds.has(matchKey)) {
            seenIds.add(matchKey);
            
            // Find the parent section
            let parentSection = null;
            for (const section of toc) {
              if (section.id === nearestHeadingId) {
                parentSection = section;
                break;
              }
              for (const sub of section.subsections || []) {
                if (sub.id === nearestHeadingId) {
                  parentSection = section;
                  break;
                }
                for (const subsub of sub.subsubsections || []) {
                  if (subsub.id === nearestHeadingId) {
                    parentSection = section;
                    break;
                  }
                }
                if (parentSection) break;
              }
              if (parentSection) break;
            }

            // Extract a snippet around the match
            const index = elementText.toLowerCase().indexOf(lower);
            const start = Math.max(0, index - 30);
            const end = Math.min(elementText.length, index + lower.length + 70);
            let snippet = elementText.substring(start, end);
            if (start > 0) snippet = '...' + snippet;
            if (end < elementText.length) snippet = snippet + '...';
            
            // Highlight the search term in the snippet
            const highlightedSnippet = snippet.replace(
              new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
              '<strong style="background-color: #fff3cd; font-weight: 600;">$1</strong>'
            );

            results.push({
              type: 'content',
              title: snippet,
              highlightedTitle: highlightedSnippet,
              id: nearestHeadingId,
              parentId: parentSection?.id,
              fullText: elementText.substring(0, 200),
              elementText: elementText
            });
          }
        }
      });
    }

    // Sort results: headings first, then content matches
    results.sort((a, b) => {
      if (a.type === 'section' && b.type !== 'section') return -1;
      if (a.type !== 'section' && b.type === 'section') return 1;
      if (a.type === 'subsection' && b.type === 'content') return -1;
      if (a.type === 'content' && b.type === 'subsection') return 1;
      return 0;
    });

    // Limit results to prevent overwhelming the dropdown
    const limitedResults = results.slice(0, 25);
    setSearchResults(limitedResults);
    setShowDropdown(limitedResults.length > 0);
  };

  const handleResultClick = (item) => {
    setShowDropdown(false);
    setSearchQuery('');

    let targetSectionId = null;

    if (item.type === 'section') {
      targetSectionId = item.id;
      setExpandedSection(item.id);
    } else if (item.type === 'subsection') {
      targetSectionId = item.id;
      setExpandedSection(item.parentId);
    } else if (item.type === 'subsubsection') {
      targetSectionId = item.id;
      const parentSection = toc.find(section =>
        section.subsections.some(sub =>
          sub.subsubsections?.some(subsub => subsub.id === item.id)
        )
      );
      if (parentSection) {
        setExpandedSection(parentSection.id);
      }
    } else if (item.type === 'content') {
      // For content matches, expand the parent section and scroll to the heading
      targetSectionId = item.id;
      if (item.parentId) {
        setExpandedSection(item.parentId);
      }
    }

    if (targetSectionId) {
      setTimeout(() => {
        scrollToSection(targetSectionId);
      }, 200);
    }
  };

  return (
    <div className="instructions-page">
      <div className="instructions-header">
        <div className="header-content">
          <h2>Instructions for Use</h2>
          <Button
            variant="link"
            onClick={() => navigate(-1)}
            className="close-button-instructions"
          >
            ×
          </Button>
        </div>
      </div>
      <div className="instructions-container">
        <div className={`sidebar-instructions ${isSidebarOpen ? 'open' : 'closed'}`} style={{
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
          <div ref={searchInputRef} className="search-container-instructions" style={{ display: isSidebarOpen ? "block" : "none" }}>
            <input
              type="text"
              className="instructions-search-input"
              placeholder="Search instructions..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => setShowDropdown(searchResults.length > 0)}
            />
            {showDropdown && (
              <div className="instructions-search-dropdown">
                {searchResults.map((item, idx) => (
                  <div
                    key={item.id + '-' + item.type}
                    className="instructions-search-result"
                    onClick={() => handleResultClick(item)}
                  >
                    {item.highlightedTitle ? (
                      <span 
                        className={item.type === 'section' ? 'search-result-title' : item.type === 'content' ? 'search-result-content' : 'search-result-subtitle'}
                        dangerouslySetInnerHTML={{ __html: item.highlightedTitle }}
                      />
                    ) : (
                      <span className={item.type === 'section' ? 'search-result-title' : item.type === 'content' ? 'search-result-content' : 'search-result-subtitle'}>
                        {item.title}
                      </span>
                    )}
                    {item.type === 'subsection' && (
                      <span className="search-result-context">
                        ({toc.find(s => s.subsections?.some(sub => sub.id === item.id))?.title})
                      </span>
                    )}
                    {item.type === 'content' && item.parentId && (
                      <span className="search-result-context">
                        ({toc.find(s => s.id === item.parentId)?.title || 'Content'})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button
            variant="link"
            className="sidebar-instructions-toggle"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            <div className="hamburger-icon-instructions">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </Button>
          <Nav className="flex-column instructions-nav">
            {toc.map((section) => (
              <div key={section.id} className="nav-section">
                <div
                  onClick={() => {
                    setExpandedSection(expandedSection === section.id ? null : section.id);
                    scrollToSection(section.id);
                  }}
                  className={`section-header ${expandedSection === section.id ? 'active' : ''}`}
                >
                  {section.title}
                </div>
                {expandedSection === section.id && section.subsections && section.subsections.length > 0 && (
                  <div className="subsections">
                    {section.subsections.map((sub) => (
                      <div key={sub.id}>
                        <Nav.Link
                          key={sub.id}
                          onClick={e => {
                            e.stopPropagation();
                            scrollToSection(sub.id);
                          }}
                          className="subsection-link"
                        >
                          {sub.title}
                        </Nav.Link>
                        {sub.subsubsections?.length > 0 && (
                          <div style={{ marginLeft: '28px', marginTop: '0.25rem', borderLeft: '2px solid #e0e0e0' }}>
                            {sub.subsubsections.map((subsub) => (
                              <Nav.Link
                                key={subsub.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  scrollToSection(subsub.id);
                                }}
                                className="subsubsection-link"
                              >
                                {subsub.title}
                              </Nav.Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

          </Nav>
        </div>

        <main
          className="main-content-instructions"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </div>
    </div>
  );
};

export default InstructionsFromDocx;
